import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import {
  MapPin, Sun, Cloud, CloudSun, CloudRain, CloudDrizzle,
  CloudFog, CloudLightning, CloudSnow, UserCheck, ShieldCheck,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, STATUS_COLORS, bookingPill, withAlpha } from "../lib/bookingStatus";

// Icon-Key aus Open-Meteo (weather_svc.py) → Lucide-Komponente.
const WEATHER_ICON = {
  sun: Sun,
  cloud: Cloud,
  "cloud-sun": CloudSun,
  "cloud-rain": CloudRain,
  "cloud-drizzle": CloudDrizzle,
  "cloud-fog": CloudFog,
  "cloud-lightning": CloudLightning,
  snowflake: CloudSnow,
};

function formatArrivalLabel(iso) {
  if (!iso) return "—";
  const d = parseISO(iso);
  const days = differenceInCalendarDays(d, new Date());
  if (days === 0) return "Heute";
  if (days === 1) return "Morgen";
  if (days > 1 && days < 7) return `in ${days} Tagen`;
  return format(d, "EEE, dd. MMM", { locale: de });
}

function ArrivalCard({ row, showConcierges }) {
  const Icon = WEATHER_ICON[row.weather?.icon] || Cloud;
  const days = row.to_date
    ? differenceInCalendarDays(parseISO(row.to_date), new Date())
    : 99;
  const imminent = days <= 1;
  // Feb 2026 · 11 Sep 2026 · User-Directive: Kachel-Akzent = effektive
  // Buchungsstatus-Farbe (systemweit einheitliches 4-Farb-Schema). Wir
  // rufen `bookingPill` mit den Feldern auf, die `/team/next-arrivals`
  // ausliefert — genau dieselbe Truth-Source wie in Kalender + Listen.
  const pill = bookingPill({
    status: row.status,
    from_date: row.from_date,
    to_date: row.to_date,
    assigned_to: row.assigned_to,
    assigned_concierge_id: row.assigned_concierge_id,
  });
  const accent = pill.bg;
  const dateStr = row.to_date
    ? format(parseISO(row.to_date), "dd. MMM yyyy", { locale: de })
    : "—";

  // Kollabiere gleichen Haupt-/Verantwortlichen zu einer Zeile („beides").
  const conciergeRows = (() => {
    if (!showConcierges) return null;
    const head = { role: "Haupt-Concierge", id: row.assigned_concierge_id, name: row.assigned_concierge_name };
    const resp = { role: "Verantwortlich",  id: row.assigned_to,             name: row.assigned_to_name };
    if (head.id && resp.id && head.id === resp.id) {
      return [{ role: "Haupt-Concierge · Verantwortlich", name: head.name, key: "both" }];
    }
    const rows = [];
    if (head.id) rows.push({ role: head.role, name: head.name, key: "head" });
    if (resp.id) rows.push({ role: resp.role, name: resp.name, key: "resp" });
    if (!rows.length) rows.push({ role: "Verantwortlich", name: "—", key: "empty" });
    return rows;
  })();

  return (
    <Link
      to={`/team/bookings?open=${encodeURIComponent(row.booking_id)}`}
      className="bg-white border border-navy/10 p-5 hover:border-gold transition-colors block"
      data-testid={`next-arrival-card-${row.booking_id}`}
      data-imminent={imminent ? "true" : "false"}
      data-status={pill.label}
      title="Vollständige Abwesenheits-Details öffnen"
      style={{ borderLeftWidth: 4, borderLeftColor: accent }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="text-[10.5px] tracking-[0.18em] uppercase font-medium"
              style={{ color: accent }}
              data-testid={`next-arrival-relative-${row.booking_id}`}
            >
              {formatArrivalLabel(row.to_date)}
            </div>
            <span
              className="text-[10px] tracking-[0.14em] uppercase px-2 py-0.5"
              style={{ background: accent, color: pill.fg }}
              data-testid={`next-arrival-status-${row.booking_id}`}
            >
              {pill.label}
            </span>
          </div>
          <div className="font-display text-lg text-navy truncate mt-1.5">
            {row.customer_name || "—"}
          </div>
          <div
            className="text-[13px] text-navy-dark/70 truncate"
            data-testid={`next-arrival-property-${row.booking_id}`}
          >
            {row.property_name || "—"}
          </div>
          <div className="text-[11px] text-navy-dark/50 mt-1">{dateStr}</div>
        </div>
        {row.weather && (
          <div
            className="flex flex-col items-end gap-0.5 shrink-0"
            data-testid={`next-arrival-weather-${row.booking_id}`}
            title={row.weather.label || ""}
          >
            <Icon size={28} strokeWidth={1.4} style={{ color: BRAND_COLORS.navy }} />
            {row.weather.t_max !== null && row.weather.t_max !== undefined && (
              <div className="text-[11px] text-navy-dark/80 font-medium tabular-nums">
                {Math.round(row.weather.t_max)}°
                {row.weather.t_min !== null && row.weather.t_min !== undefined && (
                  <span className="text-navy-dark/40">
                    {" / "}
                    {Math.round(row.weather.t_min)}°
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {(row.property_address || row.property_city) && (
        <div className="flex items-start gap-1.5 text-[12px] text-navy-dark/60 mt-3">
          <MapPin size={12} className="mt-0.5 shrink-0" style={{ color: BRAND_COLORS.gold }} />
          <span className="truncate">
            {row.property_address}
            {row.property_city ? `, ${row.property_city}` : ""}
          </span>
        </div>
      )}
      {row.weather?.label && (
        <div className="text-[11px] text-navy-dark/50 mt-1.5 flex items-center gap-1.5">
          <span
            className="w-1 h-1 rounded-full inline-block"
            style={{ background: withAlpha(BRAND_COLORS.navy, 0.35) }}
          />
          {row.weather.label}
        </div>
      )}
      {/* Feb 2026 · 11 Sep 2026 · Admin-only: Haupt-Concierge +
          Verantwortlicher direkt auf der Kachel. Concierges sehen die
          Zeile nicht (Fokus auf eigene Aufgaben, kein Team-Kontext). */}
      {conciergeRows && (
        <div
          className="mt-3 pt-2.5 border-t border-navy/10 space-y-1"
          data-testid={`next-arrival-concierges-${row.booking_id}`}
        >
          {conciergeRows.map((c) => (
            <div
              key={c.key}
              className="flex items-center gap-1.5 text-[11.5px] leading-tight"
            >
              {c.key === "head" ? (
                <ShieldCheck size={12} className="shrink-0" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.8} />
              ) : (
                <UserCheck size={12} className="shrink-0" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.8} />
              )}
              <span className="text-navy-dark/55 tracking-[0.05em]">
                {c.role}:
              </span>
              <span className="text-navy font-medium truncate">
                {c.name || "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </Link>
  );
}

export default function NextArrivalsRadar() {
  const { user, activeConciergeId, activeConciergeName } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const url =
          activeConciergeId && user?.role === "admin"
            ? `/team/next-arrivals?limit=3&scope_concierge_id=${encodeURIComponent(activeConciergeId)}`
            : "/team/next-arrivals?limit=3";
        const { data } = await api.get(url);
        setRows(Array.isArray(data) ? data : []);
        setError(false);
      } catch (e) {
        console.warn("next-arrivals fetch failed:", e);
        setRows([]);
        setError(true);
      }
    })();
    // Poll every 10 min so status stays fresh — cheap thanks to server-side cache
    const iv = setInterval(async () => {
      try {
        const url =
          activeConciergeId && user?.role === "admin"
            ? `/team/next-arrivals?limit=3&scope_concierge_id=${encodeURIComponent(activeConciergeId)}`
            : "/team/next-arrivals?limit=3";
        const { data } = await api.get(url);
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        /* silent */
      }
    }, 10 * 60 * 1000);
    return () => clearInterval(iv);
  }, [user?.id, user?.role, activeConciergeId]);

  if (rows === null) return null;

  const isConciergeMode =
    user?.role === "concierge" ||
    (user?.role === "admin" && activeConciergeId);
  // Feb 2026 · User-Directive „Deine nächste Anreise umbenennen in
  // Deine nächste Abreise" — Concierges denken in Abwesenheits-Enden
  // (customer verlässt sein Objekt, Concierge übernimmt). Die Datenbasis
  // (booking.to_date) bleibt identisch, nur die Beschriftung wechselt.
  const title = isConciergeMode
    ? "Deine nächsten Abreisen"
    : "Nächste Abreisen";
  const scopeHint =
    user?.role === "admin" && activeConciergeName
      ? ` · ${activeConciergeName}`
      : "";

  return (
    <section
      className="mb-8"
      data-testid="next-arrivals-widget"
      data-count={rows.length}
      data-scope-concierge={activeConciergeId || ""}
    >
      <div className="flex items-baseline justify-between mb-4 gap-4">
        <div className="min-w-0">
          <div className="label-caps mb-1">Abreise-Radar{scopeHint}</div>
          <h2 className="font-display text-xl md:text-2xl text-navy truncate">
            {title}
          </h2>
        </div>
        <Link
          to="/team/calendar"
          className="text-[11px] tracking-[0.15em] uppercase text-navy border-b border-gold hover:text-gold shrink-0"
          data-testid="next-arrivals-calendar-link"
        >
          Kalender →
        </Link>
      </div>

      {rows.length === 0 ? (
        <div
          className="bg-white border border-navy/10 p-6 text-[13px] text-navy-dark/60"
          data-testid="next-arrivals-empty"
        >
          {error
            ? "Abreise-Radar konnte nicht geladen werden."
            : "Aktuell keine Abreise in Sicht."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {rows.map((r) => (
            <ArrivalCard
              key={r.booking_id}
              row={r}
              showConcierges={user?.role === "admin"}
            />
          ))}
        </div>
      )}
    </section>
  );
}
